// Lightweight, dependency-free rich-text editor for task notes.
//
// Uses a contentEditable surface + document.execCommand for the formatting
// toolbar (bold / italic / underline / strikethrough / lists / headings).
// Emojis and symbols are inserted at the caret; links can be added via the
// toolbar OR by simply pasting a URL — pasted URLs are auto-linkified and their
// visible label is shortened (ClickUp-style). All content is sanitized on
// commit (and again on read) via sanitizeHtml — see that module for the XSS
// rationale.

import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Smile,
  RemoveFormatting,
} from "lucide-preact";
import { sanitizeHtml, notesToSafeHtml } from "../lib/sanitizeHtml";
import { shortenUrl, URL_RE } from "../lib/linkify";

/** Shared prose styling so the editor and the read-only view render alike.
 *
 * GRÖSSEN-SONDERFALL: der `[&_code]`-Eintrag unten trägt eine relative
 * 0,85-em-Größe, nicht aus der Skala (text-xs/sm/…). `PROSE_CLASS` wird an ganz
 * unterschiedlichen Basis-Schriftgrößen eingehängt (Editor, Kommentar-Feed,
 * Task-Beschreibung); `em` bleibt dort IMMER "etwas kleiner als der
 * umgebende Text", ein fester Skalenwert wäre je nach Einbettung zu groß
 * oder zu klein. */
export const PROSE_CLASS =
  "text-sm leading-relaxed text-[color:var(--color-on-surface)] " +
  "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 " +
  "[&_li]:my-0.5 [&_p]:my-1 [&_h1]:text-lg [&_h1]:font-bold [&_h1]:my-1.5 " +
  "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:my-1.5 [&_h3]:font-semibold [&_h3]:my-1 " +
  "[&_b]:font-semibold [&_strong]:font-semibold [&_blockquote]:border-l-2 " +
  "[&_blockquote]:border-[color:var(--color-outline-variant)] [&_blockquote]:pl-3 [&_blockquote]:text-[color:var(--color-on-surface-variant)] " +
  "[&_code]:rounded [&_code]:bg-[color:var(--color-surface-container)] [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em] " +
  "[&_a]:text-[color:var(--color-primary)] [&_a]:underline [&_a]:break-words";

const EMOJIS = [
  "😀", "😅", "🎉", "✅", "❗", "❓", "⭐", "🔥", "👍", "👎",
  "💡", "📌", "📅", "⚠️", "❤️", "🚀", "✨", "🙌", "🤝", "📈",
  "🐛", "🔒", "📝", "⏰", "💬",
];
const SYMBOLS = [
  "→", "←", "↑", "↓", "⇒", "•", "★", "✓", "✗", "…",
  "—", "±", "×", "÷", "§", "€", "™", "°", "≈", "≠",
];

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Turns plain pasted text into HTML, auto-linkifying + shortening URLs. */
function linkifyPlain(text: string): string {
  // Escape everything by default; only the recognized URL spans become <a>
  // tags. (The naive `text.replace(URL_RE, ...)` left non-URL text
  // unescaped, so pasted text containing "<"/"&" was parsed as HTML instead
  // of shown literally.)
  let out = "";
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    const url = m[0];
    const index = m.index ?? 0;
    out += escapeHtml(text.slice(last, index));
    const href = escapeHtml(url);
    const label = escapeHtml(shortenUrl(url));
    out += `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">${label}</a>`;
    last = index + url.length;
  }
  out += escapeHtml(text.slice(last));
  return out.replace(/\r?\n/g, "<br>");
}

interface Props {
  /** Current stored notes value (HTML or legacy plain text). */
  value: string | null | undefined;
  /** Changes when a different record loads — forces a re-init of the surface. */
  docKey: string | number;
  readOnly?: boolean;
  placeholder?: string;
  /** Called on blur with the sanitized HTML (null when empty). */
  onCommit: (html: string | null) => void;
}

export function RichTextEditor(props: Props): preact.JSX.Element {
  const { value, docKey, readOnly, placeholder, onCommit } = props;

  if (readOnly) return <RichTextView value={value} placeholder={placeholder} />;

  const ref = useRef<HTMLDivElement>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [empty, setEmpty] = useState(!value);
  // Active inline-format states at the caret (drives aria-pressed on the
  // toolbar). Updated on selection changes while the editor owns the selection.
  const [fmt, setFmt] = useState({ bold: false, italic: false, underline: false, strike: false });

  useEffect(() => {
    const onSel = (): void => {
      const el = ref.current;
      if (!el || !el.contains(document.getSelection()?.anchorNode ?? null)) return;
      try {
        setFmt({
          bold: document.queryCommandState("bold"),
          italic: document.queryCommandState("italic"),
          underline: document.queryCommandState("underline"),
          strike: document.queryCommandState("strikeThrough"),
        });
      } catch {
        /* queryCommandState is deprecated and may throw in exotic engines. */
      }
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  // (Re)initialize the surface whenever the edited record changes. Guarded so a
  // re-render for the SAME record never wipes the caret mid-edit.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = notesToSafeHtml(value);
    setEmpty(el.textContent?.trim() === "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey]);

  // Close the emoji picker on outside click.
  useEffect(() => {
    if (!showPicker) return;
    const onDoc = (e: MouseEvent): void => {
      if (!ref.current?.parentElement?.contains(e.target as Node)) setShowPicker(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showPicker]);

  function focusEditor(): void {
    ref.current?.focus();
  }

  function exec(command: string, arg?: string): void {
    focusEditor();
    try {
      document.execCommand("styleWithCSS", false, "false");
    } catch {
      /* not supported everywhere — falls back to default tag output */
    }
    document.execCommand(command, false, arg);
    refreshEmpty();
  }

  function insertHtmlAtCaret(html: string): void {
    focusEditor();
    document.execCommand("insertHTML", false, html);
    refreshEmpty();
  }

  function refreshEmpty(): void {
    setEmpty(ref.current?.textContent?.trim() === "");
  }

  function handleLink(): void {
    const sel = window.getSelection();
    const hasText = !!sel && !sel.isCollapsed && !!ref.current?.contains(sel.anchorNode);
    const url = window.prompt("Link-URL einfügen:", "https://");
    if (!url || url.trim() === "" || url.trim() === "https://") return;
    const href = url.trim();
    if (hasText) {
      exec("createLink", href); // wraps the selection; sanitize adds target/rel
    } else {
      insertHtmlAtCaret(
        `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer nofollow">${escapeHtml(
          shortenUrl(href)
        )}</a>&nbsp;`
      );
    }
  }

  function handlePaste(e: ClipboardEvent): void {
    const dt = e.clipboardData;
    if (!dt) return;
    e.preventDefault();
    const html = dt.getData("text/html");
    const text = dt.getData("text/plain");
    // Prefer sanitized rich HTML; fall back to auto-linkified plain text.
    const payload = html && html.trim() ? sanitizeHtml(html) : linkifyPlain(text ?? "");
    insertHtmlAtCaret(payload);
  }

  function commit(): void {
    const el = ref.current;
    if (!el) return;
    const clean = sanitizeHtml(el.innerHTML).trim();
    // Normalize "empty" contentEditable artifacts (e.g. <br>, &nbsp;) to null.
    const isBlank = el.textContent?.trim() === "" && !/<(img|a|ul|ol)\b/i.test(clean);
    onCommit(isBlank ? null : clean);
  }

  return (
    <div class="flex max-h-[24rem] flex-col overflow-hidden rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)]">
      {/* Toolbar — stays outside the scrollable text area below, so it never moves. */}
      <div class="relative flex flex-wrap items-center gap-0.5 border-b border-[color:var(--color-outline-variant)] px-1.5 py-1">
        <TbBtn title="Fett (Ctrl+B)" pressed={fmt.bold} onClick={() => exec("bold")}><Bold size={16} /></TbBtn>
        <TbBtn title="Kursiv (Ctrl+I)" pressed={fmt.italic} onClick={() => exec("italic")}><Italic size={16} /></TbBtn>
        <TbBtn title="Unterstrichen (Ctrl+U)" pressed={fmt.underline} onClick={() => exec("underline")}><Underline size={16} /></TbBtn>
        <TbBtn title="Durchgestrichen" pressed={fmt.strike} onClick={() => exec("strikeThrough")}><Strikethrough size={16} /></TbBtn>
        <TbSep />
        <TbBtn title="Aufzählung" onClick={() => exec("insertUnorderedList")}><List size={16} /></TbBtn>
        <TbBtn title="Nummerierte Liste" onClick={() => exec("insertOrderedList")}><ListOrdered size={16} /></TbBtn>
        <TbSep />
        <TbBtn title="Link einfügen" onClick={handleLink}><LinkIcon size={16} /></TbBtn>
        <TbBtn title="Emoji / Symbol" onClick={() => setShowPicker((v) => !v)}><Smile size={16} /></TbBtn>
        <TbSep />
        <TbBtn title="Formatierung entfernen" onClick={() => exec("removeFormat")}><RemoveFormatting size={16} /></TbBtn>

        {showPicker && (
          <div
            class="absolute left-0 top-full z-10 mt-1 w-64 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-2 shadow-level-3"
            onMouseDown={(e) => e.preventDefault()}
          >
            <div class="grid grid-cols-10 gap-0.5">
              {[...EMOJIS, ...SYMBOLS].map((ch) => (
                <button
                  key={ch}
                  type="button"
                  class="rounded p-1 text-base leading-none hover:bg-[color:var(--color-surface-container)]"
                  onClick={() => {
                    insertHtmlAtCaret(escapeHtml(ch));
                    setShowPicker(false);
                  }}
                >
                  {ch}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Editable surface — scrolls internally so the toolbar above never moves. */}
      <div class="relative flex-1 overflow-y-auto">
        {empty && placeholder && (
          <span class="pointer-events-none absolute left-3 top-2.5 text-sm text-[color:var(--color-on-surface-variant)]">
            {placeholder}
          </span>
        )}
        <div
          ref={ref}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label="Beschreibung"
          onInput={refreshEmpty}
          onBlur={commit}
          onPaste={handlePaste}
          class={`min-h-[7rem] w-full px-3 py-2.5 focus:outline-none ${PROSE_CLASS}`}
        />
      </div>
    </div>
  );
}

/** Read-only rendering of stored notes (always sanitized before injection). */
export function RichTextView(props: {
  value: string | null | undefined;
  placeholder?: string;
}): preact.JSX.Element {
  const html = notesToSafeHtml(props.value);
  if (!html) {
    return (
      <p class="text-sm italic text-[color:var(--color-on-surface-variant)]">
        {props.placeholder ?? "—"}
      </p>
    );
  }
  return <div class={PROSE_CLASS} dangerouslySetInnerHTML={{ __html: html }} />;
}

// ---- toolbar helpers ----

function TbBtn(props: {
  title: string;
  onClick: () => void;
  /** Toggle state (bold/italic/… active at the caret) — sets aria-pressed. */
  pressed?: boolean;
  children: preact.ComponentChildren;
}): preact.JSX.Element {
  return (
    <button
      type="button"
      title={props.title}
      aria-label={props.title}
      aria-pressed={props.pressed}
      // preventDefault keeps the editor selection while the button is pressed.
      onMouseDown={(e) => e.preventDefault()}
      onClick={props.onClick}
      class={`rounded p-1.5 hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] ${
        props.pressed
          ? "bg-[color:var(--color-surface-container)] text-[color:var(--color-on-surface)]"
          : "text-[color:var(--color-on-surface-variant)]"
      }`}
    >
      {props.children}
    </button>
  );
}

function TbSep(): preact.JSX.Element {
  return <span class="mx-0.5 h-4 w-px bg-[color:var(--color-outline-variant)]" aria-hidden="true" />;
}
